<template>
	<div
		v-if="forceShow || audioTracks.length > 1"
		ref="audioTracksMenuControl"
		class="player-controls__menu-control"
		@focusout="handleFocusOut"
	>
		<button
			ref="audioTracksMenuButton"
			v-tooltip="tooltip"
			type="button"
			class="player-controls__button"
			:aria-label="$I18n.trans('view_mode.language')"
			aria-haspopup="dialog"
			:aria-controls="menuId"
			:aria-expanded="showMenu ? 'true' : 'false'"
			@click.stop="handleClick"
			@keydown="handleTriggerKeyDown"
			@keyup="handleTriggerKeyUp"
		>
			<Icon
				icon-name="headset"
				:size="controlSize"
			/>
		</button>
		<div
			:id="menuId"
			ref="audioTracksMenu"
			class="player-controls__menu -audio"
			:class="{ 'is-visible': showMenu }"
			role="dialog"
			:aria-label="$I18n.trans('view_mode.language')"
			@keydown.esc.stop.prevent="handleClose"
		>
			<PlayerControlsSettingsMenu
				v-if="showMenu"
				ref="playerControlsAudioTracksMenu"
				:size="controlSize"
				:menu-label="$I18n.trans('view_mode.language')"
				v-bind="menuProps"
				@set-audio-track="(value) => $emit('set-audio-track', value)"
				@close="handleClose"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon.vue';
import PlayerControlsSettingsMenu from './PlayerControlsSettingsMenu.vue';
import { VTooltip } from 'v-tooltip';

export default {
	name:       'AudioTracksButton',
	components: {
		Icon,
		PlayerControlsSettingsMenu,
	},
	directives: {
		tooltip: VTooltip,
	},
	props: {
		audioTracks: {
			type:    Array,
			default: () => [],
		},
		currentAudioTrackId: {
			type:    Number,
			default: 0,
		},
		controlSize: {
			type:     String,
			required: true,
		},
		forceShow: {
			type:    Boolean,
			default: false,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
		isTouchDevice: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			showMenu: false,
			menuId:   `player-audio-tracks-menu-${this._uid}`,
		};
	},
	computed: {
		menuProps() {
			return {
				variant:             this.isMobileWebApp ? 'mobile' : 'default',
				audioTrackOptions:   this.audioTracks,
				currentAudioTrackId: this.currentAudioTrackId,
			};
		},
		tooltip() {
			if (this.isTouchDevice || this.showMenu) return {};

			return {
				content: this.$I18n.trans('view_mode.language'),
				classes: `-size-${this.controlSize} tooltip`,
				trigger: 'hover',
			};
		},
	},
	methods: {
		handleTriggerKeyDown(event) {
			if (event.key === 'Escape' && this.showMenu) {
				event.preventDefault();
				event.stopPropagation();
				this.handleClose();
				return;
			}
			if (!['Enter', ' '].includes(event.key)) return;
			event.preventDefault();
			event.stopPropagation();
			this.handleClick();
		},
		handleTriggerKeyUp(event) {
			if (!this.showMenu || !['Enter', ' '].includes(event.key)) return;
			event.preventDefault();
			event.stopPropagation();
			this.focusMenuAfterToggle();
		},
		handleClick() {
			this.showMenu = !this.showMenu;
			this.$emit('menu-toggle', this.showMenu);

			if (this.showMenu) {
				this.$emit('opened');
				this.focusMenuAfterToggle();

				const interval = setInterval(() => {
					if (this.showMenu) {
						this.$emit('still-open');
					} else {
						clearInterval(interval);
					}
				}, 2000);
			}

			if (this.$refs.playerControlsAudioTracksMenu) {
				this.$refs.playerControlsAudioTracksMenu.resetToMainMenu();
			}
		},
		handleClose(returnFocus = true) {
			this.showMenu = false;
			this.$emit('menu-toggle', false);
			this.$emit('close');
			if (returnFocus) this.$nextTick(() => this.$refs.audioTracksMenuButton?.focus());
		},
		handleFocusOut() {
			setTimeout(() => {
				if (this.showMenu && !this.$refs.audioTracksMenuControl?.contains(document.activeElement)) this.closeMenu(false);
			}, 0);
		},
		closeMenu(returnFocus = true) {
			if (this.showMenu) {
				this.handleClose(returnFocus);
			}
		},
		// Wait until native button activation is complete so it cannot reclaim focus from the popup.
		focusMenuAfterToggle() {
			this.$nextTick(() => {
				setTimeout(() => this.$refs.audioTracksMenu?.querySelector('button:not(:disabled)')?.focus(), 0);
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.player-controls__menu-control {
	position: relative;
	display: flex;
}

.player-controls__button {
	display: flex;
	color: var(--color-white);
	line-height: 1;
	cursor: pointer;

	:deep(.player-controls.-dark) & {
		color: var(--color-black);
	}
}

.player-controls__menu {
	position: absolute;
	display: flex;
	flex-direction: column;
	visibility: hidden;
	opacity: 0;
	transition: visibility 0.2s, opacity 0.2s ease-in-out;
	text-align: center;
	bottom: 32px;
	right: -16px;

	&.is-visible {
		visibility: visible;
		opacity: 1;
	}
}
</style>
